import { useState, useEffect, useRef } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";

const stripePromise = loadStripe("pk_live_51T6PRqGj3nnQolv43mhtOtfQ34vQsfIjtG3bnx2QbUp7KfEhFkChwVc40Tg6vuxsIFSWcFdEQF9qGNzfzUXPNyKO00QmmfJY43".replace(/ /g, ""));
const API = "https://temboswift-backend.onrender.com/api";
const G = { green: "#0b5e35", greenDark: "#093d28", greenMid: "#1a7a4a", greenLight: "#f0faf5", acc: "#f5a623", bg: "#f5f0e8", white: "#fff", border: "#e8e3d8", text: "#111", muted: "#666", light: "#999", red: "#dc2626", redLight: "#fef2f2", font: "'DM Sans', sans-serif" };

const COUNTRIES = [
  { code: "KE", name: "Kenya", dial: "+254", flag: "🇰🇪", currency: "KES" },
  { code: "UG", name: "Uganda", dial: "+256", flag: "🇺🇬", currency: "UGX" },
  { code: "TZ", name: "Tanzania", dial: "+255", flag: "🇹🇿", currency: "TZS" },
  { code: "RW", name: "Rwanda", dial: "+250", flag: "🇷🇼", currency: "RWF" },
  { code: "NG", name: "Nigeria", dial: "+234", flag: "🇳🇬", currency: "NGN" },
  { code: "GH", name: "Ghana", dial: "+233", flag: "🇬🇭", currency: "GHS" },
];

function req(path, opts = {}) {
  const token = localStorage.getItem("ts_token");
  const headers = { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  return fetch(`${API}${path}`, { ...opts, headers: { ...headers, ...opts.headers } }).then(async r => {
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || "Request failed");
    return data;
  });
}

function CountrySelector({ selected, onSelect }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = COUNTRIES.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.dial.includes(search));

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button onClick={() => setOpen(!open)} style={{ display: "flex", alignItems: "center", gap: 8, background: G.bg, border: `1.5px solid ${open ? G.green : G.border}`, borderRadius: 10, padding: "10px 14px", cursor: "pointer", fontFamily: G.font, whiteSpace: "nowrap" }}>
        <span style={{ fontSize: 22 }}>{selected.flag}</span>
        <span style={{ fontSize: 14, fontWeight: 700 }}>{selected.dial}</span>
        <i className={`fas fa-chevron-${open ? "up" : "down"}`} style={{ fontSize: 10, color: G.muted }}></i>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, background: G.white, border: `1px solid ${G.border}`, borderRadius: 14, boxShadow: "0 8px 32px rgba(0,0,0,0.12)", zIndex: 100, width: 240, maxHeight: 280, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <div style={{ padding: "10px 12px", borderBottom: `1px solid ${G.border}` }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search country..." autoFocus style={{ width: "100%", border: `1px solid ${G.border}`, borderRadius: 8, padding: "8px 10px", fontSize: 13, outline: "none", fontFamily: G.font, boxSizing: "border-box" }} />
          </div>
          <div style={{ overflowY: "auto" }}>
            {filtered.map(c => (
              <div key={c.code} onClick={() => { onSelect(c); setOpen(false); setSearch(""); }}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 14px", cursor: "pointer", background: selected.code === c.code ? G.greenLight : "transparent" }}>
                <span style={{ fontSize: 22 }}>{c.flag}</span>
                <div style={{ flex: 1 }}><div style={{ fontSize: 14, fontWeight: 600 }}>{c.name}</div><div style={{ fontSize: 12, color: G.muted }}>{c.dial}</div></div>
                {selected.code === c.code && <i className="fas fa-check" style={{ color: G.green, fontSize: 12 }}></i>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Payment form using Stripe Elements
function PaymentForm({ clientSecret, amount, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setLoading(true); setError("");
    try {
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) }
      });
      if (stripeError) { setError(stripeError.message); setLoading(false); return; }
      if (paymentIntent.status === "succeeded") { onSuccess(paymentIntent); }
    } catch (e) { setError(e.message); setLoading(false); }
  };

  return (
    <div>
      {error && <div style={{ background: G.redLight, color: G.red, padding: "10px 14px", borderRadius: 10, marginBottom: 12, fontSize: 13 }}><i className="fas fa-exclamation-circle"></i> {error}</div>}
      <div style={{ background: G.white, border: `1.5px solid ${G.green}`, borderRadius: 10, padding: "14px 16px", marginBottom: 16 }}>
        <CardElement options={{ style: { base: { fontSize: "16px", color: G.text, fontFamily: G.font, "::placeholder": { color: G.muted } } } }} />
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={onCancel} style={{ flex: 1, padding: "14px", borderRadius: 100, border: `1px solid ${G.border}`, background: "transparent", color: G.muted, fontWeight: 700, cursor: "pointer", fontFamily: G.font }}>
          <i className="fas fa-arrow-left"></i> Back
        </button>
        <button onClick={handlePay} disabled={loading || !stripe} style={{ flex: 2, padding: "14px", borderRadius: 100, background: G.green, color: "#fff", border: "none", fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1, fontFamily: G.font, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          {loading ? <><i className="fas fa-spinner fa-spin"></i> Processing...</> : <><i className="fas fa-lock"></i> Pay ${amount}</>}
        </button>
      </div>
      <div style={{ textAlign: "center", fontSize: 12, color: G.muted, marginTop: 12 }}>
        <i className="fas fa-shield-alt"></i> Secured by Stripe · 256-bit encryption
      </div>
    </div>
  );
}

export default function SendMoney({ user, onDone, setPage }) {
  const [step, setStep] = useState(1);
  const [usd, setUsd] = useState("");
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [country, setCountry] = useState(COUNTRIES[0]);
  const [phoneLocal, setPhoneLocal] = useState("");
  const [mpesaName, setMpesaName] = useState(null);
  const [nameLoading, setNameLoading] = useState(false);
  const [nameError, setNameError] = useState("");
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [savedRecipients, setSavedRecipients] = useState([]);
  const [deliveryMethod, setDeliveryMethod] = useState("mpesa");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [transferData, setTransferData] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);

  const fullPhone = country.dial + phoneLocal.replace(/^0/, "");

  useEffect(() => {
    const amt = parseFloat(usd);
    if (!amt || amt < 0.5) { setQuote(null); return; }
    setQuoteLoading(true);
    const t = setTimeout(() => {
      req(`/transfers/quote?amount=${amt}`).then(setQuote).catch(() => {}).finally(() => setQuoteLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [usd]);

  useEffect(() => {
    req("/recipients").then(d => setSavedRecipients(d.recipients || [])).catch(() => {});
  }, []);

  const lookupName = async (localPhone) => {
    const cleaned = localPhone.replace(/\s|-/g, "");
    if (cleaned.length < 8) { setMpesaName(null); setNameError(""); return; }
    setNameLoading(true); setNameError(""); setMpesaName(null); setSelectedRecipient(null);
    await new Promise(r => setTimeout(r, 1000));
    const full = country.dial + cleaned.replace(/^0/, "");
    const saved = savedRecipients.find(r => r.phone.replace(/\s/g, "").includes(cleaned.slice(-9)));
    if (saved) { setMpesaName({ name: saved.full_name, verified: true }); setSelectedRecipient(saved); }
    else if (cleaned.length >= 9) { setMpesaName({ name: "M-Pesa User", verified: false }); }
    else { setNameError("Enter a valid phone number"); }
    setNameLoading(false);
  };

  const createTransfer = async () => {
    if (user?.kyc_status !== "approved") { setError("Identity verification required. Go to Account → KYC Verification."); return; }
    setLoading(true); setError("");
    try {
      let recipientId = selectedRecipient?.id;
      if (!recipientId) {
        const r = await req("/recipients", { method: "POST", body: JSON.stringify({ full_name: mpesaName?.name || "Recipient", phone: fullPhone, delivery_method: deliveryMethod }) });
        recipientId = r.recipient?.id || r.id;
      }
      const result = await req("/transfers", { method: "POST", body: JSON.stringify({ recipient_id: recipientId, amount_usd: parseFloat(usd) }) });
      setTransferData(result.transfer);
      setClientSecret(result.clientSecret);
      setStep(4); // Payment step
    } catch (e) { setError(e.message || "Transfer failed"); }
    finally { setLoading(false); }
  };

  const onPaymentSuccess = (paymentIntent) => {
    setSuccess(true);
  };

  if (success) return (
    <div style={{ textAlign: "center", padding: "48px 20px", fontFamily: G.font }}>
      <div style={{ width: 80, height: 80, background: G.greenLight, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
        <i className="fas fa-check-circle" style={{ fontSize: 40, color: G.green }}></i>
      </div>
      <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 8 }}>Money Sent! 🎉</div>
      <div style={{ fontSize: 32, fontWeight: 900, color: G.green, marginBottom: 4 }}>KES {quote ? parseFloat(quote.amount_kes).toLocaleString() : ""}</div>
      <div style={{ fontSize: 15, color: G.muted, marginBottom: 8 }}>to {mpesaName?.name || selectedRecipient?.full_name}</div>
      <div style={{ fontSize: 13, color: G.muted, marginBottom: 8 }}>{country.flag} {fullPhone}</div>
      <div style={{ background: G.greenLight, borderRadius: 12, padding: "12px 20px", marginBottom: 32, fontSize: 13, color: G.green }}>
        <i className="fas fa-bolt"></i> Arriving in ~2 minutes via {deliveryMethod === "mpesa" ? "M-Pesa" : "Bank Transfer"}
      </div>
      <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
        <button onClick={() => { setSuccess(false); setStep(1); setPhoneLocal(""); setMpesaName(null); setSelectedRecipient(null); setClientSecret(null); setTransferData(null); }}
          style={{ background: G.green, color: "#fff", border: "none", borderRadius: 100, padding: "14px 28px", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: G.font }}>
          <i className="fas fa-plus"></i> Send Another
        </button>
        <button onClick={() => onDone && onDone()}
          style={{ background: G.white, color: G.text, border: `1px solid ${G.border}`, borderRadius: 100, padding: "14px 28px", fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: G.font }}>
          Home
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ fontFamily: G.font, maxWidth: 480 }}>
      {/* Steps */}
      <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 28 }}>
        {["Amount", "Recipient", "Review", "Pay"].map((label, i) => (
          <div key={label} style={{ display: "flex", alignItems: "center", flex: i < 3 ? 1 : 0 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", background: step > i + 1 ? G.green : step === i + 1 ? G.green : G.border, color: step >= i + 1 ? "#fff" : G.muted, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700 }}>
                {step > i + 1 ? <i className="fas fa-check"></i> : i + 1}
              </div>
              <div style={{ fontSize: 10, fontWeight: 600, color: step === i + 1 ? G.green : G.muted }}>{label}</div>
            </div>
            {i < 3 && <div style={{ flex: 1, height: 2, background: step > i + 1 ? G.green : G.border, margin: "0 6px", marginBottom: 18 }}></div>}
          </div>
        ))}
      </div>

      {error && <div style={{ background: G.redLight, border: `1px solid ${G.red}33`, borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: G.red }}><i className="fas fa-exclamation-circle"></i> {error}</div>}

      {/* STEP 1 - Amount */}
      {step === 1 && (
        <div>
          <div style={{ background: G.white, borderRadius: 16, border: `1px solid ${G.border}`, padding: 20, marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: G.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>YOU SEND</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <span style={{ fontSize: 32, fontWeight: 900, color: G.green }}>$</span>
              <input value={usd} onChange={e => setUsd(e.target.value.replace(/[^0-9.]/g, ""))} type="text" inputMode="decimal"
                style={{ flex: 1, border: "none", outline: "none", fontSize: 48, fontWeight: 900, color: G.text, fontFamily: G.font, background: "transparent", minWidth: 0 }} />
              <div style={{ background: G.bg, borderRadius: 10, padding: "8px 12px", display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 18 }}>🇺🇸</span>
                <span style={{ fontSize: 14, fontWeight: 700 }}>USD</span>
              </div>
            </div>
            </div>

          {quoteLoading && <div style={{ background: G.white, borderRadius: 16, border: `1px solid ${G.border}`, padding: 16, marginBottom: 12, textAlign: "center", color: G.muted, fontSize: 13 }}><i className="fas fa-spinner fa-spin"></i> Getting live rate...</div>}

          {quote && !quoteLoading && (
            <div style={{ background: `linear-gradient(135deg, ${G.greenDark}, ${G.greenMid})`, borderRadius: 16, padding: 20, marginBottom: 12, color: "#fff" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", marginBottom: 4 }}>RECIPIENT GETS</div>
                  <div style={{ fontSize: 28, fontWeight: 900 }}>KES {parseFloat(quote.amount_kes).toLocaleString()}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", marginBottom: 4 }}>DELIVERY</div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>📱 ~2 min</div>
                </div>
              </div>
              <div style={{ background: "rgba(255,255,255,0.1)", borderRadius: 10, padding: "12px 16px" }}>
                {[["Exchange rate", `1 USD = ${parseFloat(quote.client_rate).toFixed(2)} KES`], ["Transfer fee", "Free ✓"], ["Total you pay", `$${usd} USD`]].map(([k, v]) => (
                  <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0" }}>
                    <span style={{ color: "rgba(255,255,255,0.7)" }}>{k}</span>
                    <span style={{ fontWeight: 600 }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <button onClick={() => setStep(2)} disabled={!quote || parseFloat(usd) < 2}
            style={{ width: "100%", background: G.green, color: "#fff", border: "none", borderRadius: 100, padding: "16px", fontSize: 16, fontWeight: 700, cursor: !quote || parseFloat(usd) < 2 ? "not-allowed" : "pointer", opacity: !quote || parseFloat(usd) < 2 ? 0.6 : 1, fontFamily: G.font, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            Continue <i className="fas fa-arrow-right"></i>
          </button>
        </div>
      )}

      {/* STEP 2 - Recipient */}
      {step === 2 && (
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 20 }}>Who are you sending to?</div>

          {savedRecipients.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: G.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>Recent Recipients</div>
              <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 4 }}>
                {savedRecipients.map(r => (
                  <div key={r.id} onClick={() => { setSelectedRecipient(r); setPhoneLocal(r.phone.replace(country.dial, "")); setMpesaName({ name: r.full_name, verified: true }); }}
                    style={{ flexShrink: 0, textAlign: "center", cursor: "pointer" }}>
                    <div style={{ width: 52, height: 52, borderRadius: "50%", background: selectedRecipient?.id === r.id ? G.green : G.greenLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 800, color: selectedRecipient?.id === r.id ? "#fff" : G.green, margin: "0 auto 6px", border: `2px solid ${selectedRecipient?.id === r.id ? G.green : "transparent"}` }}>
                      {r.full_name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: selectedRecipient?.id === r.id ? G.green : G.text, maxWidth: 60, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.full_name.split(" ")[0]}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ borderTop: `1px solid ${G.border}`, margin: "16px 0", position: "relative" }}>
                <span style={{ position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)", background: G.bg, padding: "0 8px", fontSize: 12, color: G.muted }}>or enter number</span>
              </div>
            </div>
          )}

          <div style={{ fontSize: 11, fontWeight: 700, color: G.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>Phone Number</div>
          <div style={{ background: G.white, borderRadius: 14, border: `1.5px solid ${mpesaName?.verified ? G.green : G.border}`, padding: "14px 16px", marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: G.muted, fontWeight: 600, marginBottom: 8 }}>COUNTRY & PHONE</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <CountrySelector selected={country} onSelect={(c) => { setCountry(c); setPhoneLocal(""); setMpesaName(null); setNameError(""); }} />
              <input value={phoneLocal} onChange={e => { setPhoneLocal(e.target.value); lookupName(e.target.value); setSelectedRecipient(null); }}
                placeholder="7XX XXX XXX" type="tel"
                style={{ flex: 1, border: "none", outline: "none", fontSize: 18, fontWeight: 600, color: G.text, fontFamily: G.font, background: "transparent", minWidth: 0 }} />
              {nameLoading && <i className="fas fa-spinner fa-spin" style={{ color: G.muted, flexShrink: 0 }}></i>}
              {mpesaName?.verified && <i className="fas fa-check-circle" style={{ color: G.green, fontSize: 20, flexShrink: 0 }}></i>}
            </div>
            {phoneLocal && <div style={{ fontSize: 12, color: G.muted, marginTop: 6 }}>Full: {fullPhone}</div>}
          </div>

          {mpesaName && !nameLoading && (
            <div style={{ background: mpesaName.verified ? G.greenLight : "#fef3c7", border: `1px solid ${mpesaName.verified ? G.green : "#f59e0b"}33`, borderRadius: 12, padding: "14px 16px", marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: "50%", background: mpesaName.verified ? G.green : "#d97706", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 16, fontWeight: 700, flexShrink: 0 }}>
                  {mpesaName.name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 800 }}>{mpesaName.name}</div>
                  <div style={{ fontSize: 12, color: mpesaName.verified ? G.green : "#92400e", fontWeight: 600, marginTop: 2 }}>
                    {mpesaName.verified ? <><i className="fas fa-check-circle"></i> Verified account</> : <><i className="fas fa-info-circle"></i> Unverified</>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {nameError && <div style={{ fontSize: 13, color: G.red, marginBottom: 12 }}><i className="fas fa-exclamation-circle"></i> {nameError}</div>}

          {/* Delivery method */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: G.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>Delivery Method</div>
            <div style={{ display: "flex", gap: 10 }}>
              {[{id:"mpesa",icon:"fa-mobile-alt",label:"M-Pesa"},{id:"bank",icon:"fa-university",label:"Bank Transfer"}].map(m => (
                <button key={m.id} onClick={() => setDeliveryMethod(m.id)}
                  style={{ flex: 1, padding: "12px", borderRadius: 12, border: `2px solid ${deliveryMethod === m.id ? G.green : G.border}`, background: deliveryMethod === m.id ? G.greenLight : "#fff", color: deliveryMethod === m.id ? G.green : G.muted, fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: G.font, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  <i className={`fas ${m.icon}`}></i> {m.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setStep(1)} style={{ flex: 1, padding: "14px", borderRadius: 100, border: `1px solid ${G.border}`, background: "transparent", color: G.muted, fontWeight: 700, cursor: "pointer", fontFamily: G.font }}>
              <i className="fas fa-arrow-left"></i> Back
            </button>
            <button onClick={() => setStep(3)} disabled={!mpesaName && !selectedRecipient}
              style={{ flex: 2, padding: "14px", borderRadius: 100, background: G.green, color: "#fff", border: "none", fontWeight: 700, cursor: !mpesaName && !selectedRecipient ? "not-allowed" : "pointer", opacity: !mpesaName && !selectedRecipient ? 0.6 : 1, fontFamily: G.font, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              Review <i className="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>
      )}

      {/* STEP 3 - Confirm */}
      {step === 3 && (
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 20 }}>Review Transfer</div>
          <div style={{ background: `linear-gradient(135deg, ${G.greenDark}, ${G.greenMid})`, borderRadius: 20, padding: 24, marginBottom: 16, color: "#fff" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", marginBottom: 4 }}>YOU PAY</div>
                <div style={{ fontSize: 36, fontWeight: 900 }}>${usd}</div>
              </div>
              <i className="fas fa-arrow-right" style={{ fontSize: 20, color: "rgba(255,255,255,0.5)" }}></i>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", marginBottom: 4 }}>THEY RECEIVE</div>
                <div style={{ fontSize: 24, fontWeight: 900, color: "#4cde8f" }}>KES {quote ? parseFloat(quote.amount_kes).toLocaleString() : ""}</div>
              </div>
            </div>
            <div style={{ background: "rgba(255,255,255,0.1)", borderRadius: 12, padding: "14px 16px" }}>
              {[["Rate", `1 USD = ${quote ? parseFloat(quote.client_rate).toFixed(2) : "..."} KES`], ["Fee", "Free ✓"], ["Delivery", deliveryMethod === "mpesa" ? "M-Pesa ~2 min" : "Bank Transfer"], ["Recipient", mpesaName?.name || selectedRecipient?.full_name || "—"], ["Phone", fullPhone]].map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "5px 0", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                  <span style={{ color: "rgba(255,255,255,0.7)" }}>{k}</span>
                  <span style={{ fontWeight: 600 }}>{v}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setStep(2)} style={{ flex: 1, padding: "14px", borderRadius: 100, border: `1px solid ${G.border}`, background: "transparent", color: G.muted, fontWeight: 700, cursor: "pointer", fontFamily: G.font }}>
              <i className="fas fa-arrow-left"></i> Back
            </button>
            <button onClick={createTransfer} disabled={loading}
              style={{ flex: 2, padding: "14px", borderRadius: 100, background: G.green, color: "#fff", border: "none", fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1, fontFamily: G.font, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              {loading ? <><i className="fas fa-spinner fa-spin"></i> Preparing...</> : <><i className="fas fa-credit-card"></i> Proceed to Pay</>}
            </button>
          </div>
        </div>
      )}

      {/* STEP 4 - Payment */}
      {step === 4 && clientSecret && (
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>Pay ${usd}</div>
          <div style={{ fontSize: 14, color: G.muted, marginBottom: 20 }}>Enter your card details to complete the transfer</div>
          <div style={{ background: G.greenLight, border: `1px solid ${G.green}22`, borderRadius: 12, padding: "12px 16px", marginBottom: 20, display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontSize: 13, color: G.green, fontWeight: 600 }}>Sending to {mpesaName?.name || "Recipient"}</span>
            <span style={{ fontSize: 13, fontWeight: 800, color: G.green }}>KES {quote ? parseFloat(quote.amount_kes).toLocaleString() : ""}</span>
          </div>
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <PaymentForm
              clientSecret={clientSecret}
              amount={usd}
              onSuccess={onPaymentSuccess}
              onCancel={() => setStep(3)}
            />
          </Elements>
        </div>
      )}
    </div>
  );
}








